import React, { useMemo } from 'react';
import moment from 'moment-jalaali';
import '../SellerInvoice.css';
import { toPersianDigits } from '../../../StorePanel/StoreInvoice/utils';

// تابع کمکی فرمت شماره کارت (۴ رقم - ۴ رقم)
const formatCardNumber = (cardNo) => {
    if (!cardNo) return '-';
    const cleaned = String(cardNo).replace(/\D/g, '');
    const formatted = cleaned.replace(/(\d{4})(?=\d)/g, '$1 - ');
    return toPersianDigits(formatted);
};

// تابع کمکی فرمت تاریخ شمسی با ساعت
const formatFullShamsiDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return new Intl.DateTimeFormat('fa-IR', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit',
    }).format(date);
};

export default function InvoicePrintContent({ invoiceData, contentRef }) {
    // ۱. تمامی هوک‌ها بدون هیچ شرط و returnی در بالاترین سطح کامپوننت تعریف می‌شوند
    const isRepealSearch = Boolean(invoiceData?.IsRepealSearch);

    const statusId = Number(invoiceData?.invoiceStatusId || invoiceData?.status_id);
    
    // اگر IsRepealSearch فعال باشد، لیبل پستی و حواله مقداری غیرفعال می‌شوند و فاکتور کامل چاپ می‌شود
    const isLabel = !isRepealSearch && statusId === 4;       // وضعیت ۴: لیبل پستی
    const isDeliveryOnly = !isRepealSearch && statusId === 3; // وضعیت ۳: حواله انبار مقداری

    // چک کردن وجود تراکنش‌های مالی
    const transactions = invoiceData?.finance_trnsactions || invoiceData?.transactions || [];
    const hasTransactions = !isLabel && !isDeliveryOnly && transactions.length > 0;

    // محاسبات مالی با تفکیک دقیق ارسال و بسته‌بندی
    const summary = useMemo(() => {
        if (!invoiceData) return {
            totalConsumer: 0,
            totalDiscount: 0,
            totalVat: 0,
            sendCost: 0,
            totalPackCost: 0,
            totalShipping: 0,
            finalTotal: 0
        };

        const details = invoiceData.invoice_details || [];
        let totalConsumer = 0;
        let totalDiscount = 0;
        let totalVat = 0;
        let totalPackCost = 0;

        details.forEach(prod => {
            totalConsumer += Number(prod.totalPrice || 0);
            totalDiscount += Number(prod.discount_price || 0);
            totalVat += Number(prod.vat_price || 0);
            totalPackCost += Number(prod.pack_cost || 0);
        });

        const sendCost = Number(invoiceData.send_cost_price || 0);
        const totalShipping = sendCost + totalPackCost;
        const finalTotal = Number(
            invoiceData.total_price_invoice_archive ||
            (totalConsumer - totalDiscount + totalVat + totalShipping)
        );

        return { totalConsumer, totalDiscount, totalVat, sendCost, totalPackCost, totalShipping, finalTotal };
    }, [invoiceData]);

    const totalQuantity = useMemo(() => {
        if (!invoiceData?.invoice_details) return 0;
        return invoiceData.invoice_details.reduce((acc, item) => acc + Number(item.amount || 0), 0);
    }, [invoiceData]);

    // رندر هوشمند هزینه ارسال و بسته‌بندی (بدون برچسب بسته‌بندی)
    const renderShippingFee = () => {
        const { sendCost, totalPackCost, totalShipping } = summary;

        if (sendCost === 0) {
            if (totalPackCost > 0) {
                return (
                    <div style={{ display: 'flex', flexDirection: 'column', textAlign: 'left', gap: '2px' }}>
                        <div>
                            <span style={{ fontWeight: 'bold' }}>
                                {toPersianDigits(totalPackCost.toLocaleString())} تومان
                            </span>
                        </div>
                        <div style={{ color: '#dc2626', fontWeight: 'bold', fontSize: '0.92em', marginTop: '1px' }}>
                            + پس‌کرایه (پرداخت در محل)
                        </div>
                    </div>
                );
            }
            return <span style={{ color: '#dc2626', fontWeight: 'bold' }}>پس‌کرایه (پرداخت در محل)</span>;
        }

        return (
            <span>
                <strong style={{ fontWeight: 'bold' }}>
                    {toPersianDigits(totalShipping.toLocaleString())}
                </strong> تومان
            </span>
        );
    };

    // ۲. بررسی وجود اطلاعات پس از اجرای تمام هوک‌ها (طبق قوانین ری‌اکت)
    if (!invoiceData) return null;

    return (
        <div className={`seller-print-wrapper mode-${isLabel ? 'label' : 'thermal'}`}>
            <main id="receipt-content" ref={contentRef} className="seller-print-body">
                {isLabel ? (
                    /* قالب لیبل پستی اصلاح شده برای PDF */
                    <div className="label-print-sheet" style={{ border: '2px solid #000', padding: '20px', margin: '10px', borderRadius: '8px' }}>
                        <div className="label-header" style={{ borderBottom: '2px solid #000', paddingBottom: '10px', marginBottom: '15px' }}>
                            <h3 style={{ margin: '0 0 5px 0', fontSize: '18px' }}>اطلاعات مرسوله پستی</h3>
                            <span className="factor-tag">شماره: {toPersianDigits(invoiceData.factorNumber)}</span>
                        </div>
                        <div className="label-content" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <div className="label-field" style={{ display: 'flex', gap: '8px', fontSize: '16px' }}>
                                <span className="label-title" style={{ fontWeight: 'bold', minWidth: '80px' }}>فرستنده:</span>
                                <span className="label-val">{invoiceData.business?.title || 'فروشگاه'}</span>
                            </div>
                            <div className="label-field" style={{ display: 'flex', gap: '8px', fontSize: '16px' }}>
                                <span className="label-title" style={{ fontWeight: 'bold', minWidth: '80px' }}>گیرنده:</span>
                                <span className="label-val bold" style={{ fontSize: '18px' }}>{invoiceData.buyer?.name}</span>
                            </div>
                            <div className="label-field" style={{ display: 'flex', gap: '8px', fontSize: '16px' }}>
                                <span className="label-title" style={{ fontWeight: 'bold', minWidth: '80px' }}>همراه:</span>
                                <span className="label-val" dir="ltr">{toPersianDigits(invoiceData.buyer?.mobile)}</span>
                            </div>
                            <div className="label-field" style={{ display: 'flex', gap: '8px', fontSize: '16px' }}>
                                <span className="label-title" style={{ fontWeight: 'bold', minWidth: '80px' }}>کد پستی:</span>
                                <span className="label-val" dir="ltr">{toPersianDigits(invoiceData.buyer?.postCode || '---')}</span>
                            </div>
                            <div className="label-field full-width" style={{ marginTop: '10px', borderTop: '1px dashed #666', paddingTop: '10px' }}>
                                <div className="label-title" style={{ fontWeight: 'bold', marginBottom: '5px' }}>آدرس کامل:</div>
                                <p className="label-address" style={{ margin: 0, fontSize: '15px', lineHeight: '1.6' }}>
                                    {invoiceData.buyer?.address || 'آدرسی ثبت نشده است.'}
                                </p>
                            </div>
                        </div>
                    </div>
                ) : (
                    /* فاکتور فروش کامل یا حواله مقداری */
                    <div className="invoice-print-sheet">
                        <div className="invoice-head">
                            <div className="head-brand">
                                <h2>{invoiceData.business?.title || 'فاکتور فروش کالا'}</h2>
                                <span className="badge-type">
                                    {isDeliveryOnly
                                        ? 'حواله انبار و تحویل کالا (مقداری)'
                                        : (isRepealSearch ? 'صورتحساب فروش کالا' : 'صورتحساب فروش کالا')}
                                </span>
                            </div>

                            <div className="head-meta-row">
                                <div className="meta-item">
                                    <span>شماره فاکتور:</span>
                                    <strong>{toPersianDigits(invoiceData.factorNumber)}</strong>
                                </div>
                                <div className="meta-item">
                                    <span>تاریخ:</span>
                                    <strong>{toPersianDigits(moment(invoiceData.date).format('jYYYY/jMM/jDD'))}</strong>
                                    <span className="meta-divider">|</span>
                                    <span>زمان:</span>
                                    <strong>{toPersianDigits(moment(invoiceData.date).format('HH:mm'))}</strong>
                                </div>
                            </div>
                        </div>

                        <div className="invoice-parties">
                            <div className="party-row">
                                <span className="p-label">خریدار:</span>
                                <span className="p-val">{invoiceData.buyer?.name}</span>
                            </div>
                            <div className="party-row">
                                <span className="p-label">شماره همراه:</span>
                                <span className="p-val" dir="ltr">{toPersianDigits(invoiceData.buyer?.mobile)}</span>
                            </div>
                            {invoiceData.buyer?.postCode && (
                                <div className="party-row">
                                    <span className="p-label">کد پستی:</span>
                                    <span className="p-val" dir="ltr">{toPersianDigits(invoiceData.buyer?.postCode)}</span>
                                </div>
                            )}
                            {invoiceData.buyer?.address && (
                                <div className="party-row address-row">
                                    <span className="p-label">آدرس تحویل:</span>
                                    <span className="p-val">{invoiceData.buyer?.address}</span>
                                </div>
                            )}
                        </div>

                        <div className="table-wrapper">
                            <table className="items-table">
                                <thead>
                                    <tr>
                                        <th className="th-idx">ردیف</th>
                                        <th className="th-name">شرح کالا / خدمات</th>
                                        <th className="th-qty">تعداد</th>
                                        {!isDeliveryOnly && (
                                            <>
                                                <th className="th-price">قیمت واحد (تومان)</th>
                                                <th className="th-disc">تخفیف (تومان)</th>
                                                <th className="th-total">جمع جزء (تومان)</th>
                                            </>
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {invoiceData.invoice_details?.map((prod, idx) => (
                                        <tr key={prod.id || idx}>
                                            <td className="td-idx">{toPersianDigits(idx + 1)}</td>
                                            <td className="td-name">{prod.commodityName}</td>
                                            <td className="td-qty">{toPersianDigits(prod.amount)}</td>
                                            {!isDeliveryOnly && (
                                                <>
                                                    <td className="td-price">{toPersianDigits(Number(prod.price || 0).toLocaleString())}</td>
                                                    <td className="td-disc">{toPersianDigits(Number(prod.discount_price || 0).toLocaleString())}</td>
                                                    <td className="td-total">
                                                        {toPersianDigits((Number(prod.totalPrice || 0) - Number(prod.discount_price || 0)).toLocaleString())}
                                                    </td>
                                                </>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {isDeliveryOnly ? (
                            <div className="delivery-summary-box">
                                <span>مجموع تعداد اقلام:</span>
                                <strong>{toPersianDigits(totalQuantity)}</strong>
                            </div>
                        ) : (
                            <div className="financial-summary-section">
                                <div className="fin-row">
                                    <span className="fin-label">مجموع قیمت مصرف‌کننده:</span>
                                    <span className="fin-value">{toPersianDigits(Number(summary.totalConsumer || 0).toLocaleString())} تومان</span>
                                </div>

                                {Number(summary.totalDiscount) > 0 && (
                                    <div className="fin-row profit-highlight">
                                        <span className="fin-label">مجموع تخفیف:</span>
                                        <span className="fin-value">{toPersianDigits(Number(summary.totalDiscount).toLocaleString())} تومان</span>
                                    </div>
                                )}

                                {Number(summary.totalVat) > 0 && (
                                    <div className="fin-row">
                                        <span className="fin-label">مالیات بر ارزش افزوده:</span>
                                        <span className="fin-value">{toPersianDigits(Number(summary.totalVat).toLocaleString())} تومان</span>
                                    </div>
                                )}

                                <div className="fin-row" style={{ alignItems: 'flex-start' }}>
                                    <span className="fin-label" style={{ paddingTop: '2px' }}>هزینه بسته‌بندی و ارسال:</span>
                                    <span className="fin-value">
                                        {renderShippingFee()}
                                    </span>
                                </div>

                                <div className="fin-divider"></div>

                                <div className="fin-row final-total-row">
                                    <span className="fin-label">مبلغ کل پرداختی:</span>
                                    <span className="fin-value">{toPersianDigits(Number(summary.finalTotal || 0).toLocaleString())} تومان</span>
                                </div>
                            </div>
                        )}

                        {/* اطلاعات تراکنش و پرداخت */}
                        {hasTransactions && (
                            <div className="seller-payment-section">
                                <div className="seller-payment-title">اطلاعات پرداخت و تسویه</div>
                                {transactions.map((tx, index) => {
                                    const isCardToCard = String(tx.transactionType_id) === "1";
                                    const refCode = tx.referenceId || tx.refId || tx.trackingCode;

                                    return (
                                        <div key={tx.id || index} className="seller-payment-card">
                                            <div className="seller-payment-grid">
                                                <div>
                                                    <span className="label">مبلغ: </span>
                                                    <span className="val success">{toPersianDigits(Number(tx.totalPrice || 0).toLocaleString())} تومان</span>
                                                </div>
                                                <div>
                                                    <span className="label">تاریخ: </span>
                                                    <span className="val">{toPersianDigits(formatFullShamsiDate(tx.date))}</span>
                                                </div>

                                                {isCardToCard ? (
                                                    <>
                                                        <div className="payment-card-full-row">
                                                            <span className="label">کارت مبدا: </span>
                                                            <span className="val card-number-text" dir="ltr">{formatCardNumber(tx.sourceCardNumber)}</span>
                                                        </div>
                                                        <div className="payment-card-full-row">
                                                            <span className="label">کارت مقصد: </span>
                                                            <span className="val card-number-text" dir="ltr">{formatCardNumber(tx.destinationCardNumber)}</span>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <>
                                                        <div>
                                                            <span className="label">روش پرداخت: </span>
                                                            <span className="val bold">{tx.gatewayName || 'پرداخت اینترنتی'}</span>
                                                        </div>
                                                        <div>
                                                            <span className="label">وضعیت: </span>
                                                            <span className="val success">موفق</span>
                                                        </div>
                                                        {refCode && (
                                                            <div className="payment-card-full-row">
                                                                <span className="label">شناسه پیگیری / مرجع: </span>
                                                                <span className="val bold tracking-code" dir="ltr">{toPersianDigits(refCode)}</span>
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                    </div>
                )}
            </main>
        </div>
    );
}
